import { isRouteErrorResponse, Link, useRouteError } from 'react-router'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

/**
 * UI-1: the router's `errorElement` — the last line of defence for anything
 * that throws while rendering below the router. Without it a single throw in
 * one page paints react-router's stock white "Unexpected Application Error"
 * screen, stack trace and all, with no way back into the app.
 *
 * Three cases, in the order they actually happen in production:
 *
 *  1. A STALE CHUNK. Every page here is code-split (`lazy` in App.tsx), so a
 *     redeploy while a session is open leaves the browser asking for a hashed
 *     asset that no longer exists. This is by far the most common real trigger
 *     and it is NOT a fault — the copy says so and the only fix is a full
 *     document load, which re-fetches the new asset manifest.
 *  2. A route error RESPONSE (`isRouteErrorResponse`) — a status thrown by a
 *     loader/action. None of our routes use loaders today, but the boundary
 *     must not degrade to "Something went wrong" if one ever does.
 *  3. Anything else — the house fallback copy, identical to the string
 *     `useAsync` shows on a failed fetch so the two never read as different
 *     kinds of failure.
 *
 * INVARIANT: raw error text/stack renders ONLY under `import.meta.env.DEV`.
 * Vite inlines that literal, so the block is dead-code-eliminated from a
 * production build — internals cannot leak to a user even by accident.
 */

/**
 * The three shapes a failed dynamic import takes across browsers — Chromium,
 * Firefox and Safari each word it differently, and the message is the only
 * signal we get (the rejection is a plain TypeError with no code).
 */
const STALE_CHUNK = /dynamically imported module|Importing a module script failed|Failed to fetch dynamically imported/i

/** `useRouteError()` is `unknown` — anything can be thrown, including non-Errors. */
function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return ''
}

function stackOf(error: unknown): string | null {
  return error instanceof Error && typeof error.stack === 'string' ? error.stack : null
}

export function RouteErrorBoundary() {
  const error = useRouteError()
  const message = messageOf(error)
  const isStale = STALE_CHUNK.test(message)

  let title = 'Something went wrong'
  let description = 'Please try again.'
  let action = 'Try again'

  if (isStale) {
    title = 'This page was updated'
    description = 'Reload to continue.'
    action = 'Reload'
  } else if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = 'Page not found'
      description = 'That page does not exist, or it has moved.'
    } else if (error.status === 401 || error.status === 403) {
      title = 'You do not have access to this page'
      description = 'Ask your HR admin if you think this is wrong.'
    } else if (error.status >= 500) {
      description = 'The server could not complete that request. Please try again.'
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Alert variant="destructive">
          <AlertTitle>{title}</AlertTitle>
          <AlertDescription>{description}</AlertDescription>
        </Alert>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {/* Both actions reload the CURRENT url rather than re-rendering in
              place: react-router keeps an errorElement mounted until the next
              successful navigation, and navigating to the url we are already
              on is a no-op. A stale chunk needs the full document load anyway. */}
          <Button size="sm" onClick={() => window.location.reload()}>
            {action}
          </Button>
          {/* A real navigation — this is what actually clears the boundary.
              <Link> (not <a>) so the subpath deploy's basename is applied. */}
          <Button asChild variant="outline" size="sm">
            <Link to="/">Back to home</Link>
          </Button>
        </div>

        {/* DEV ONLY — never shipped, see the INVARIANT above. */}
        {import.meta.env.DEV && (stackOf(error) ?? message) ? (
          <pre className="mt-4 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-muted p-3 text-xs text-muted-foreground">
            {stackOf(error) ?? message}
          </pre>
        ) : null}
      </div>
    </div>
  )
}
